import React, { useEffect, useMemo, useState } from 'react';

import { stringifyEntityRef } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity, useEntityOwnership } from '@backstage/plugin-catalog-react';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

import { AIToolsAboutContent } from './AIToolsAboutContent';

const useStyles = makeStyles({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)',
    marginBottom: '10px',
  },
  gridItemCardContent: {
    flex: 1,
  },
  sectionTitle: {
    fontWeight: 700,
    letterSpacing: 0.3,
    marginBottom: 8,
  },
  detailsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  detailPill: {
    minWidth: 180,
    flex: '1 1 220px',
    border: '1px solid #dfe3e8',
    borderRadius: 10,
    padding: '8px 10px',
    background: '#fafbfc',
  },
  detailLabel: {
    color: '#52606d',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.2,
    fontSize: '0.68rem',
  },
  detailValue: {
    color: '#1f2d3d',
    fontWeight: 500,
    wordBreak: 'break-word',
    marginTop: 2,
    lineHeight: 1.3,
  },
  statusChip: {
    fontWeight: 600,
    borderRadius: 6,
    marginTop: 2,
  },
  sectionDivider: {
    margin: '14px 0',
  },
  externalLink: {
    color: '#005a9e',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

const AI_ANNOTATIONS = {
  type: 'ai-catalog.tomtom.com/type',
  status: 'ai-catalog.tomtom.com/status',
  wentInProductionDate: 'ai-catalog.tomtom.com/went-in-production-date',
  slack: 'ai-catalog.tomtom.com/slack',
  vettingSecurity: 'ai-catalog.tomtom.com/vetting-security',
  securityTicket: 'ai-catalog.tomtom.com/security-ticket',
  vettingLegal: 'ai-catalog.tomtom.com/vetting-legal',
  onitTicket: 'ai-catalog.tomtom.com/onit-ticket',
  builtBy: 'ai-catalog.tomtom.com/built-by',
  boughtBy: 'ai-catalog.tomtom.com/bought-by',
} as const;

export const AIToolsAboutCard = () => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const { entity } = useEntity();
  const { isOwnedEntity } = useEntityOwnership();
  const annotations = entity.metadata.annotations ?? {};
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    let mounted = true;

    identityApi
      .getProfileInfo()
      .then(profile => {
        if (mounted) {
          setUserEmail((profile.email ?? '').toLowerCase());
        }
      })
      .catch(() => {
        if (mounted) {
          setUserEmail('');
        }
      });

    return () => {
      mounted = false;
    };
  }, [identityApi]);

  const aiToolsAdmins =
    configApi
      .getOptionalStringArray('app.aiToolsAdmins')
      ?.map(admin => admin.toLowerCase()) ?? [];
  const isAiToolsAdmin = !!userEmail && aiToolsAdmins.includes(userEmail);

  const canDelete = isAiToolsAdmin || isOwnedEntity(entity);
  const ownerValue = entity.spec?.owner;
  const owner = typeof ownerValue === 'string' ? ownerValue : '';
  const canEdit =
    isAiToolsAdmin ||
    isOwnedEntity(entity) ||
    owner.toLowerCase() === 'unknown';

  const editUrl = useMemo(() => {
    const currentAnnotations = entity.metadata.annotations ?? {};
    const currentOwnerValue = entity.spec?.owner;
    const currentOwner =
      typeof currentOwnerValue === 'string' ? currentOwnerValue : '';

    const formData = {
      name: entity.metadata.name,
      description: entity.metadata.description ?? '',
      type: currentAnnotations[AI_ANNOTATIONS.type] ?? '',
      status: currentAnnotations[AI_ANNOTATIONS.status] ?? '',
      wentInProductionDate:
        currentAnnotations[AI_ANNOTATIONS.wentInProductionDate] ?? '',
      owner: currentOwner,
      slack: currentAnnotations[AI_ANNOTATIONS.slack] ?? '',
      vettingSecurity: currentAnnotations[AI_ANNOTATIONS.vettingSecurity] ?? '',
      securityTicket: currentAnnotations[AI_ANNOTATIONS.securityTicket] ?? '',
      vettingLegal: currentAnnotations[AI_ANNOTATIONS.vettingLegal] ?? '',
      onitTicket: currentAnnotations[AI_ANNOTATIONS.onitTicket] ?? '',
      builtBy: currentAnnotations[AI_ANNOTATIONS.builtBy] ?? '',
      boughtBy: currentAnnotations[AI_ANNOTATIONS.boughtBy] ?? '',
      isAiToolsAdmin: String(isAiToolsAdmin),
    };

    return `/create/templates/default/edit-ai-tool-catalog-entry?formData=${encodeURIComponent(
      JSON.stringify(formData),
    )}`;
  }, [entity, isAiToolsAdmin]);

  const deleteTemplateUrl = useMemo(() => {
    const formData = {
      name: entity.metadata.name,
      entityRef: stringifyEntityRef(entity),
      isAiToolsAdmin: String(isAiToolsAdmin),
    };

    return `/create/templates/default/delete-ai-tool-catalog-entry?formData=${encodeURIComponent(
      JSON.stringify(formData),
    )}`;
  }, [entity, isAiToolsAdmin]);

  const isUrl = (value: string) => /^https?:\/\//i.test(value);
  const slackChannelUrl = (value: string) => {
    const channel = value.trim().replace(/^#/, '');

    if (!channel) {
      return '';
    }

    return `https://tomtomslack.slack.com/app_redirect?channel=${encodeURIComponent(
      channel,
    )}`;
  };

  const renderSlack = (value: string | undefined) => {
    if (!value?.trim()) {
      return '-';
    }

    if (isUrl(value)) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.externalLink}
        >
          {value}
        </a>
      );
    }

    const href = slackChannelUrl(value);

    if (!href) {
      return value;
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.externalLink}
      >
        {value.startsWith('#') ? value : `#${value.trim()}`}
      </a>
    );
  };

  const display = (value: string | undefined) => {
    if (!value?.trim()) {
      return '-';
    }

    if (isUrl(value)) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.externalLink}
        >
          {value}
        </a>
      );
    }

    return value;
  };

  const statusValue = annotations[AI_ANNOTATIONS.status] ?? '';
  const isInProductionStatus =
    statusValue.trim().toLowerCase() === 'in production';

  const detailItems = [
    { label: 'Status', key: AI_ANNOTATIONS.status },
    ...(isInProductionStatus
      ? [
          {
            label: 'Went In Production',
            key: AI_ANNOTATIONS.wentInProductionDate,
          },
        ]
      : []),
    { label: 'Type', key: AI_ANNOTATIONS.type },
    { label: 'Slack', key: AI_ANNOTATIONS.slack },
    { label: 'Security Vetting', key: AI_ANNOTATIONS.vettingSecurity },
    { label: 'Legal Vetting', key: AI_ANNOTATIONS.vettingLegal },
    { label: 'Security Ticket', key: AI_ANNOTATIONS.securityTicket },
    { label: 'ONIT Ticket', key: AI_ANNOTATIONS.onitTicket },
    { label: 'Built By', key: AI_ANNOTATIONS.builtBy },
    { label: 'Bought By', key: AI_ANNOTATIONS.boughtBy },
  ] as const;

  return (
    <Card className={classes.gridItemCard}>
      <CardHeader
        title="About"
        action={
          <>
            <IconButton
              component={Link}
              aria-label="Edit"
              title={
                canEdit
                  ? 'Edit Metadata'
                  : 'Only owner users or AI Tools admins can edit this AI tool'
              }
              disabled={!canEdit}
              to={canEdit ? editUrl : '#'}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              component={Link}
              aria-label="Delete"
              title={
                canDelete
                  ? 'Delete Entity'
                  : 'Only owner users or AI Tools admins can delete this AI tool'
              }
              disabled={!canDelete}
              to={canDelete ? deleteTemplateUrl : '#'}
            >
              <DeleteIcon />
            </IconButton>
          </>
        }
      />
      <Divider />
      <CardContent className={classes.gridItemCardContent}>
        <AIToolsAboutContent entity={entity} />
        <Divider className={classes.sectionDivider} />
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          AI Tool Details
        </Typography>
        <div className={classes.detailsWrap}>
          {detailItems.map(item => (
            <div key={item.label} className={classes.detailPill}>
              <Typography variant="body2" className={classes.detailLabel}>
                {item.label}
              </Typography>
              {item.key === AI_ANNOTATIONS.status ? (
                <Chip
                  size="small"
                  label={annotations[AI_ANNOTATIONS.status] || 'Unknown'}
                  color="primary"
                  variant="outlined"
                  className={classes.statusChip}
                />
              ) : (
                <Typography variant="body2" className={classes.detailValue}>
                  {item.key === AI_ANNOTATIONS.slack
                    ? renderSlack(annotations[item.key])
                    : display(annotations[item.key])}
                </Typography>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
